import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { newUuid, uuidSerializer } from "@megaron/uuid";

import { customerCategories } from "./categories";

export const interactionTypes = ["call", "visit", "training", "hotline", "other"] as const;
export type InteractionType = (typeof interactionTypes)[number];

export const interactionMentionSerializer = Serializers.object({
  user: Serializers.string,
  tagPosition: Serializers.integer,
  tagLength: Serializers.integer,
});

export type InteractionMention = SerializerValue<typeof interactionMentionSerializer>;

export const interactionSerializer = Serializers.object({
  uuid: uuidSerializer,
  customerId: uuidSerializer,
  ownerEmail: Serializers.string,
  type: Serializers.stringOneOf(...interactionTypes).nullable(),
  message: Serializers.string,
  interactionDate: Serializers.datetime,
  samples: Serializers.array(Serializers.string),
  mentions: interactionMentionSerializer.array(),
  durationInMinutes: Serializers.integer,
  customerCategories: Serializers.stringOneOf(...customerCategories).array(),
  googleCalendarEventId: Serializers.string.nullable(),
});

export type Interaction = SerializerValue<typeof interactionSerializer>;

export const saveInteractionBodySerializer = Serializers.object({
  interactionId: uuidSerializer.optional(),
  customerId: uuidSerializer,
  type: Serializers.stringOneOf(...interactionTypes),
  message: Serializers.string,
  interactionDate: Serializers.datetime,
  samples: Serializers.array(Serializers.string),
  mentions: interactionMentionSerializer.array(),
  durationInMinutes: Serializers.integer,
  googleCalendarEventId: Serializers.string.nullable(),
});

export type SaveInteractionBody = SerializerValue<typeof saveInteractionBodySerializer>;

export const newInteraction = (props: Partial<SaveInteractionBody> = {}): SaveInteractionBody => ({
  interactionId: newUuid(),
  customerId: newUuid(),
  durationInMinutes: 15,
  googleCalendarEventId: null,
  interactionDate: new Date(),
  mentions: [],
  message: "interaction message",
  type: "other",
  samples: [],
  ...props,
});

export const eventTypes = [
  "userInvited",
  "userJoined",
  "proStatusEarned",
  "rewardPurchased",
  "userRegistered",
  "accountDeleted",
  "affiliateCodeRedeemed",
  "affiliateCodeClaimed",
  "productCodeRedeemed",
] as const;
export type EventType = (typeof eventTypes)[number];

export const eventPayloadSerializer = Serializers.first(
  Serializers.object({
    type: Serializers.stringOneOf("rewardPurchased"),
    time: Serializers.datetime,
    rewardId: Serializers.string,
    purchaseUuid: uuidSerializer,
    price: Serializers.decimal,
  }),
  Serializers.object({
    type: Serializers.stringOneOf("proStatusEarned"),
    time: Serializers.datetime,
  }),
  Serializers.object({
    type: Serializers.stringOneOf("affiliateCodeClaimed"),
    time: Serializers.datetime,
  }),
  Serializers.object({
    type: Serializers.stringOneOf("affiliateCodeRedeemed"),
    time: Serializers.datetime,
    transactionUuid: uuidSerializer,
    invitedByUuid: uuidSerializer.nullable(),
    codeId: Serializers.string,
  }),
  Serializers.object({
    type: Serializers.stringOneOf("userInvited"),
    time: Serializers.datetime,
    invitedUserUuid: uuidSerializer,
    codeId: Serializers.string,
  }),
  Serializers.object({
    type: Serializers.stringOneOf("userJoined"),
    time: Serializers.datetime,
  }),
  Serializers.object({
    type: Serializers.stringOneOf("userRegistered"),
    time: Serializers.datetime,
  }),
  Serializers.object({
    type: Serializers.stringOneOf("accountDeleted"),
    time: Serializers.datetime,
  }),
);

export const customerEventSerializer = Serializers.object({
  id: uuidSerializer,
  customerUuid: Serializers.string,
  payload: eventPayloadSerializer,
});

export type EventPayload = SerializerValue<typeof eventPayloadSerializer>;
export type CustomerEvent = SerializerValue<typeof customerEventSerializer>;

export const historyItemSerializer = Serializers.first(interactionSerializer, customerEventSerializer);

export const history = {
  getInteraction: HttpAction({
    path: "/interaction/:interactionId",
    method: "get",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({
      interactionId: uuidSerializer,
    }),
    errorSerializer: Serializers.stringOneOf("InteractionNotFound", "Unauthorized"),
    valueSerializer: interactionSerializer,
  }),

  getCustomerHistory: HttpAction({
    path: "/history/customer/:customerId",
    method: "get",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({
      customerId: uuidSerializer,
    }),
    errorSerializer: Serializers.stringOneOf("CustomerNotFound", "Unauthorized"),
    valueSerializer: historyItemSerializer.array(),
  }),

  customerMentionedInteractions: HttpAction({
    path: "/interactions/mentions",
    method: "get",
    requiresAuth: true as const,
    valueSerializer: interactionSerializer.array(),
  }),

  ownerInteractionQuery: HttpAction({
    path: "/interactions/ownerQuery",
    qsSerializer: Serializers.object({
      customerUuid: uuidSerializer,
      ownerEmail: Serializers.string,
      dates: Serializers.dateOnly.array().asJsonString(),
      limit: Serializers.integer,
    }).partial(),
    valueSerializer: Serializers.paginatedList(interactionSerializer),
    requiresAuth: true,
  }),

  saveInteraction: HttpAction({
    path: "/interaction",
    method: "put",
    requiresAuth: true as const,
    bodySerializer: saveInteractionBodySerializer,
    errorSerializer: Serializers.stringOneOf("CustomerNotFound", "Unauthorized", "InteractionDateCannotBeInTheFuture"),
  }),
};
